import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Toast, ToastType } from '@/components/common/admin/mobile/Toast';
import { View, StyleSheet, Platform } from 'react-native';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { registerWebModalManager, registerWebToastManager } from '@/utils/common/alert';
import { FullWindowOverlay } from 'react-native-screens';

interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  autoHide?: boolean;
  duration?: number;
  position?: 'top' | 'bottom';
}

interface ToastContextType {
  toasts: ToastItem[];
  showToast: (options: Omit<ToastItem, 'id'>) => void;
  showSuccess: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => void;
  showError: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => void;
  showWarning: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => void;
  showInfo: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => void;
  showLoading: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => string;
  hideLoading: (id?: string) => void;
  hideToast: (id: string) => void;
  hideAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
}

interface ConfirmModalState {
  visible: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ 
  children, 
  maxToasts = 3 
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    onConfirm: () => {},
    onCancel: () => {},
  });

  const generateId = useCallback(() => {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback((options: Omit<ToastItem, 'id'>) => {
    const id = generateId();
    const newToast: ToastItem = {
      id,
      autoHide: true,
      duration: 4000,
      position: 'top',
      ...options,
    };

    setToasts(prevToasts => {
      // Remove oldest toast if we exceed maxToasts
      const updatedToasts = prevToasts.length >= maxToasts 
        ? prevToasts.slice(1) 
        : prevToasts;
      
      return [...updatedToasts, newToast];
    });
  }, [generateId, maxToasts]);

  const showSuccess = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => {
    showToast({ type: 'success', message, ...options });
  }, [showToast]);

  const showError = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => {
    showToast({ type: 'error', message, ...options });
  }, [showToast]);

  const showWarning = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => {
    showToast({ type: 'warning', message, ...options });
  }, [showToast]);

  const showInfo = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => {
    showToast({ type: 'info', message, ...options });
  }, [showToast]);

  const showLoading = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>): string => {
    const id = generateId();
    const newToast: ToastItem = {
      id,
      type: 'loading',
      message,
      autoHide: false,
      position: 'top',
      ...options,
    };

    setToasts(prevToasts => {
      // Remove oldest toast if we exceed maxToasts
      const updatedToasts = prevToasts.length >= maxToasts 
        ? prevToasts.slice(1) 
        : prevToasts;
      
      return [...updatedToasts, newToast];
    });

    return id;
  }, [generateId, maxToasts]);

  const hideLoading = useCallback((id?: string) => {
    if (id) {
      setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
    } else {
      // Hide all loading toasts if no ID is provided
      setToasts(prevToasts => prevToasts.filter(toast => toast.type !== 'loading'));
    }
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  const hideAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Register the modal and toast managers with the alert utility (web only)
  useEffect(() => {
    if (Platform.OS === 'web') {
      registerWebModalManager({
        showConfirmModal: (config) => {
          setConfirmModal({
            visible: true,
            title: config.title,
            message: config.message,
            confirmText: config.confirmText,
            cancelText: config.cancelText,
            onConfirm: config.onConfirm,
            onCancel: config.onCancel || (() => {}),
          });
        },
      });

      registerWebToastManager({
        showInfo: (message) => showInfo(message),
        showSuccess: (message) => showSuccess(message),
        showError: (message) => showError(message),
        showWarning: (message) => showWarning(message),
      });
    }
  }, [showInfo, showSuccess, showError, showWarning]);

  const contextValue: ToastContextType = {
    toasts,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    hideLoading,
    hideToast,
    hideAllToasts,
  };

  const toastOverlay = (
    <View style={styles.overlay} pointerEvents="box-none">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          type={toast.type}
          message={toast.message}
          visible={true}
          onHide={() => hideToast(toast.id)}
          autoHide={toast.autoHide}
          duration={toast.duration}
          position={toast.position}
        />
      ))}
    </View>
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {Platform.OS === 'web'
        ? toastOverlay
        : <FullWindowOverlay>{toastOverlay}</FullWindowOverlay>
      }
      
      {/* Web confirmation modal */}
      {Platform.OS === 'web' && (
        <WebConfirmModal
          visible={confirmModal.visible}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          onConfirm={() => {
            setConfirmModal(prev => ({ ...prev, visible: false }));
            confirmModal.onConfirm();
          }}
          onCancel={() => {
            setConfirmModal(prev => ({ ...prev, visible: false }));
            confirmModal.onCancel();
          }}
        />
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Convenience hook for error handling
export const useErrorToast = () => {
  const { showError } = useToast();
  
  const showApiError = useCallback((error: any, fallbackMessage?: string) => {
    // Import error handling utility
    import('@/utils/admin/errorHandling').then(({ getErrorMessage, shouldShowToast }) => {
      if (shouldShowToast(error)) {
        const message = getErrorMessage(error) || fallbackMessage || 'An error occurred';
        showError(message);
      }
    });
  }, [showError]);

  return { showApiError, showError };
};

/**
 * Renders the global toast overlay inside a React Native Modal.
 * On Android, RN Modal creates a native window above FullWindowOverlay,
 * so toasts from the main overlay are hidden. This component re-renders
 * the same toasts inside the Modal so they remain visible.
 * Only renders on Android; on iOS FullWindowOverlay already covers Modals.
 */
export const ModalToastOverlay: React.FC = () => {
  const context = useContext(ToastContext);
  if (!context || Platform.OS !== 'android') return null;

  return (
    <View style={styles.overlay} pointerEvents="box-none">
      {context.toasts.map((toast) => (
        <Toast
          key={toast.id}
          type={toast.type}
          message={toast.message}
          visible={true}
          onHide={() => context.hideToast(toast.id)}
          autoHide={toast.autoHide}
          duration={toast.duration}
          position={toast.position}
        />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});
